import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { toast } from 'react-toastify';
import { getErrorMessage, parseJwt } from '@common/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import EmpathyLogo from '@/assets/logo/empathy-logo-black.svg';
import Illustration from '@/assets/illustrations/accept_org_invite_illustration.svg';
import Image from '@/components/atoms/Image';
import Routes from '@/router/routes';
import { ECompanyThemeName } from '@/enums';
import FullPageLoader from '@/components/atoms/Loader/FullPageLoader';
import { useValidateInvitation } from '@/reactQuery/get';
import {
  useMutateAcceptOrganizationInvitation,
} from '@/reactQuery/post';
import { useAuthentication } from '@/hooks/useAuthentication';

const themeLogosList = {
  [ECompanyThemeName.DEFAULT]: (
    <Image src={TelletLogo} alt="logo" className="absolute w-24 h-fit top-6 left-5" />
  ),
  [ECompanyThemeName.EMPATHY]: (
    <Image src={EmpathyLogo} alt="logo" className="absolute w-44 h-fit top-6 left-5" />
  ),
};

function AcceptOrganizationInvitation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenAccept = searchParams.get('token');
  const { themeName } = useTheme();
  const { organizationId, workspaceId } = useParams();
  const { mutateAsync: validateInvitation } = useValidateInvitation();
  const [isLoading, setIsLoading] = useState(true);
  // Opt out of the hook's auto-redirect: when the accept page is opened by
  // a user who already has a stale auth cookie, the default auto-redirect
  // would yank them to "/" before they can accept the invitation. The hook
  // is used here only for its processAuthSuccess utility.
  const { processAuthSuccess } = useAuthentication({ skipAutoRedirect: true });

  const parsedJWTToken = parseJwt(tokenAccept || '');
  const tokenExpirationDate = new Date(parsedJWTToken.exp * 1000);
  const hasTokenExpired = tokenExpirationDate < new Date();

  const { mutateAsync: acceptInvitation, isPending } = useMutateAcceptOrganizationInvitation(
    organizationId,
    tokenAccept,
  );

  useEffect(() => {
    const checkInvitationValidity = async () => {
      if (!tokenAccept || hasTokenExpired) {
        navigate(Routes.organization_invitation_link_expired.path);
        return;
      }

      // Add a small delay to show the loader
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 1000);
      });

      // Validate invitation on backend
      try {
        await validateInvitation({
          organizationId: organizationId!,
          token: tokenAccept,
        });
        // Invitation is valid, show the form
        setIsLoading(false);
      } catch (error) {
        const axiosErr = error as AxiosError;
        const errorMessage = getErrorMessage(error);
        const statusCode = axiosErr?.response?.status ?? axiosErr?.status;

        if (errorMessage.toLowerCase().includes('already accepted')) {
          navigate(`${Routes.organization_invitation_link_expired.path}?reason=already_accepted`);
          return;
        }

        // Only redirect to "expired" for actual 4xx invitation-state
        // errors. Network/5xx errors should surface to the user — previously
        // any failure (including transient server errors) redirected to the
        // expired page, making valid invitations look dead to the user.
        const isClientError =
          typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500;
        if (isClientError) {
          navigate(Routes.organization_invitation_link_expired.path);
          return;
        }

        toast.error(
          errorMessage
            || 'We couldn\'t verify this invitation right now. Please try again.',
        );
        setIsLoading(false);
      }
    };

    checkInvitationValidity();
  }, [tokenAccept, hasTokenExpired, organizationId, navigate, validateInvitation]);

  const onSubmit = async () => {
    try {
      const response = await acceptInvitation();
      const jwt = response?.data?.token;
      const targetPath = `${Routes.organization.path}/${organizationId}${Routes.workspace.path}/${workspaceId}`;
      if (jwt) {
        // Backend returns a JWT on acceptance. Because this hook instance
        // was created with `skipAutoRedirect: true`, processAuthSuccess will
        // set up auth state (cookies, zustand, posthog) WITHOUT navigating
        // — so the navigate() below lands the user on the invited workspace
        // instead of racing with the hook's default "/" redirect.
        const authResult = await processAuthSuccess(jwt);
        if (!authResult.success) {
          toast.error('Invitation accepted, but sign-in failed. Please log in.');
          navigate(Routes.login.path);
          return;
        }
        navigate(targetPath, { replace: true });
      } else {
        // No token in response — navigate directly (user may need to log in)
        navigate(targetPath);
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage = getErrorMessage(err);

      if (axiosError?.status === StatusCodes.UNAUTHORIZED) {
        navigate(
          `${
            Routes.verification_email_link_expired.path
          }/${organizationId}?token=${tokenAccept}&inviter=${encodeURIComponent(
            searchParams.get('inviter')!,
          )}`,
        );
      } else if (
        axiosError?.status === StatusCodes.FORBIDDEN ||
        errorMessage.toLowerCase().includes('not found')
      ) {
        navigate(Routes.organization_invitation_link_expired.path);
      } else if (errorMessage.toLowerCase().includes('already accepted')) {
        navigate(`${Routes.organization_invitation_link_expired.path}?reason=already_accepted`);
      } else {
        toast.error(errorMessage || 'Something went wrong. Please try again.');
      }
    }
  };

  if (isLoading) {
    return <FullPageLoader />;
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen px-4 mx-auto text-center">
      {themeLogosList[themeName]}
      <div className="flex flex-col items-center justify-center gap-2 mx-auto text-center">
        <Image
          effect="blur"
          src={Illustration}
          alt="illustration"
          className="min500:w-full w-[450px]"
        />
        <h1 className="text-5xl font-bold mt-14 text-dark min500:text-4xl">Accept invitation</h1>
        <Button
          onClick={onSubmit}
          disabled={isPending}
          className={buttonVariants({
            variant: 'default',
            className: 'mt-10 transition-all ease-in-out hover:!bg-darker-primary',
          })}
        >
          {isPending ? 'Accepting...' : 'Accept invite'}
        </Button>
      </div>
    </div>
  );
}

export default AcceptOrganizationInvitation;
